import React, { useEffect, useRef, useState } from 'react';
import { useT } from '../../../i18n/LanguageProvider';
import { rsvp_services } from '../../../services/request/rsvp';
import { buildIcs, CardEvent, formatEventWhen, googleCalendarUrl, mapsUrl, RsvpResponse } from '../../../utils/invitation';
import { rsvpErrorKey } from '../../../utils/rsvpError';

interface Props {
	cardId: string;
	event: CardEvent;
	recipientId?: string;
	/** the recipient link's token (`t`); required by the RSVP endpoints alongside recipientId */
	token?: string;
	readOnly?: boolean;
}

const OPTIONS: { value: RsvpResponse; labelKey: string }[] = [
	{ value: 'yes', labelKey: 'rsvp.going' },
	{ value: 'maybe', labelKey: 'rsvp.maybe' },
	{ value: 'no', labelKey: 'rsvp.cantGo' },
];

const MAX_GUESTS = 10;
const MAX_NOTE = 500;

const EventPanel: React.FC<Props> = ({ cardId, event, recipientId, token, readOnly }) => {
	const { t, locale } = useT();
	const [response, setResponse] = useState<RsvpResponse | null>(null);
	const [guests, setGuests] = useState(0);
	const [note, setNote] = useState('');
	const [saved, setSaved] = useState(false);
	const [saving, setSaving] = useState(false);
	const [errorKey, setErrorKey] = useState<string | null>(null);
	const noteId = useRef(`rsvp-note-${Math.random().toString(36).slice(2)}`).current;

	const canRespond = !!recipientId && !!token && !readOnly;

	useEffect(() => {
		if (!canRespond || !recipientId || !token) return;
		let cancelled = false;
		rsvp_services
			.getMine(cardId, recipientId, token)
			.then((data) => {
				if (cancelled || !data?.rsvp) return;
				setResponse(data.rsvp.response);
				setGuests(data.rsvp.guests || 0);
				setNote(data.rsvp.note || '');
				setSaved(true);
			})
			.catch((e) => {
				if (!cancelled) setErrorKey(rsvpErrorKey(e));
			});
		return () => {
			cancelled = true;
		};
	}, [cardId, recipientId, token, canRespond]);

	const when = (() => {
		try {
			return formatEventWhen(event, locale);
		} catch {
			return new Date(event.startsAt).toLocaleString();
		}
	})();

	const choose = (value: RsvpResponse) => {
		if (!canRespond) return;
		setResponse(value);
		setSaved(false);
		setErrorKey(null);
	};

	const save = async () => {
		if (!canRespond || !recipientId || !token || !response || saving) return;
		setSaving(true);
		setErrorKey(null);
		try {
			const result = await rsvp_services.submit(cardId, recipientId, token, {
				response,
				guests: event.allowGuests ? guests : 0,
				note: note.trim(),
			});
			setResponse(result.response);
			setGuests(result.guests || 0);
			setNote(result.note || '');
			setSaved(true);
		} catch (e) {
			setErrorKey(rsvpErrorKey(e));
		} finally {
			setSaving(false);
		}
	};

	const downloadIcs = () => {
		const blob = new Blob([buildIcs(event, cardId)], { type: 'text/calendar;charset=utf-8' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = 'invitation.ics';
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		// revoking synchronously can cancel the download in some browsers
		setTimeout(() => URL.revokeObjectURL(url), 1000);
	};

	const openGoogle = () => {
		const detailsUrl = `${window.location.origin}${window.location.pathname}`;
		window.open(googleCalendarUrl(event, detailsUrl), '_blank', 'noopener,noreferrer');
	};

	const linkClass = 'underline text-jacksonsPurple break-words';
	const showForm = canRespond && !!response && !saved;

	return (
		<div className="bg-white rounded-lg shadow p-4 mb-2 text-left text-sm text-gray-800">
			<h2 className="text-base font-bold mb-1 break-words">{event.title}</h2>
			<p className="mb-1">{when}</p>
			{event.locationType === 'online' ? (
				event.onlineUrl &&
				/^https?:\/\//i.test(event.onlineUrl) && (
					<p className="mb-1">
						<a href={event.onlineUrl} target="_blank" rel="noopener noreferrer" className={linkClass}>
							{t('rsvp.joinOnline')}
						</a>
					</p>
				)
			) : (
				event.address && (
					<p className="mb-1">
						<a href={mapsUrl(event.address)} target="_blank" rel="noopener noreferrer" className={linkClass}>
							{event.address}
						</a>
					</p>
				)
			)}
			{event.hostNote && <p className="mt-2 whitespace-pre-wrap break-words text-gray-600">{event.hostNote}</p>}

			<div className="mt-3">
				<p id={`${noteId}-prompt`} className="font-semibold mb-1">
					{t('rsvp.respondPrompt')}
				</p>
				<div role="radiogroup" aria-labelledby={`${noteId}-prompt`} className="flex gap-2">
					{OPTIONS.map((o) => {
						const selected = response === o.value;
						return (
							<button
								key={o.value}
								type="button"
								role="radio"
								aria-checked={selected}
								disabled={!canRespond}
								onClick={() => choose(o.value)}
								className={`flex-1 rounded-md border px-2 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
									selected ? 'bg-jacksonsPurple text-white border-jacksonsPurple' : 'bg-white text-gray-800 border-gray-300'
								}`}
							>
								{t(o.labelKey)}
							</button>
						);
					})}
				</div>
				{!canRespond && <p className="mt-2 text-xs text-gray-500">{t('rsvp.useYourLink')}</p>}
			</div>

			{showForm && (
				<div className="mt-3">
					{event.allowGuests && (
						<div className="flex items-center justify-between mb-2">
							<span id={`${noteId}-guests`}>{t('rsvp.guests')}</span>
							<div role="group" aria-labelledby={`${noteId}-guests`} className="flex items-center gap-2">
								<button
									type="button"
									aria-label={t('rsvp.decrease')}
									disabled={guests <= 0}
									onClick={() => setGuests((g) => Math.max(0, g - 1))}
									className="w-8 h-8 rounded-full border border-gray-300 disabled:opacity-40"
								>
									-
								</button>
								<span aria-live="polite" aria-label={t('rsvp.guestsLabel')} className="w-6 text-center">
									{guests}
								</span>
								<button
									type="button"
									aria-label={t('rsvp.increase')}
									disabled={guests >= MAX_GUESTS}
									onClick={() => setGuests((g) => Math.min(MAX_GUESTS, g + 1))}
									className="w-8 h-8 rounded-full border border-gray-300 disabled:opacity-40"
								>
									+
								</button>
							</div>
						</div>
					)}
					<label htmlFor={noteId} className="block mb-1">
						{t('rsvp.note')}
					</label>
					<textarea
						id={noteId}
						value={note}
						maxLength={MAX_NOTE}
						rows={2}
						placeholder={t('rsvp.notePlaceholder')}
						onChange={(e) => setNote(e.target.value)}
						className="w-full rounded-md border border-gray-300 p-2 text-sm"
					/>
					<button
						type="button"
						onClick={save}
						disabled={saving}
						className="mt-2 w-full rounded-md bg-jacksonsPurple text-white py-2 font-medium disabled:opacity-60"
					>
						{saving ? t('rsvp.saving') : t('rsvp.save')}
					</button>
				</div>
			)}

			{canRespond && saved && (
				<div className="mt-3" role="status">
					<p className="text-green-700">{t('rsvp.thanks')}</p>
					<button type="button" onClick={() => setSaved(false)} className={`${linkClass} mt-1`}>
						{t('rsvp.change')}
					</button>
				</div>
			)}

			{errorKey && (
				<p role="alert" className="mt-2 text-red-600">
					{t(errorKey)}
				</p>
			)}

			<div className="mt-3">
				<p className="font-semibold mb-1">{t('rsvp.addToCalendar')}</p>
				<div className="flex gap-2">
					<button
						type="button"
						onClick={openGoogle}
						className="flex-1 rounded-md border border-gray-300 px-2 py-2 text-xs"
					>
						{t('rsvp.googleCalendar')}
					</button>
					<button
						type="button"
						onClick={downloadIcs}
						className="flex-1 rounded-md border border-gray-300 px-2 py-2 text-xs"
					>
						{t('rsvp.appleOutlook')}
					</button>
				</div>
			</div>
		</div>
	);
};

export default EventPanel;
